import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Link,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AlertComponent from '../../../components/core/AlertComponent/AlertComponent';
import AlertService from '../../../components/core/AlertComponent/AlertService';
import { PopupMenu } from '../../../components/core/PopupMenu/PopupMenu';
import {
  AcademyAccessDeniedError,
  getTeacherAcademyDashboard,
  TeacherAcademyDashboard,
} from './AcademyService';
import {
  applyAcademySkillFinishedUpdate,
  type AcademySkillFinishedMessage,
  useAcademyRealtime,
} from './AcademyRealtime';
import { RecommendedLessons } from '../../../components/academy/RecommendedLessons/RecommendedLessons';
import { SkillPaths } from '../../../components/academy/SkillPaths/SkillPaths';
import { TierProgress } from '../../../components/academy/TierProgress/TierProgress';
import {
  getDefaultTierKey,
  getEffectiveTierKey,
  getTierView,
} from '../../../components/academy/Utilities/academyView';
import { academyClassNames, academyStyles } from './Academy.styles';
import {
  ACADEMY_AREA_MENU_ARIA_LABEL,
  ACADEMY_LOADING_MESSAGE,
  ACADEMY_NO_ACTIVE_PLAN_MESSAGE,
  ACADEMY_NO_SKILLS_TITLE,
  ACADEMY_NO_SKILLS_WITH_PLAN_MESSAGE,
  ACADEMY_REQUEST_FAILED_MESSAGE,
  ACADEMY_UNAVAILABLE_TITLE,
  ACADEMY_WELCOME_HELP_URL,
  ACADEMY_WELCOME_LINK_TEXT,
  ACADEMY_WELCOME_TEXT_AFTER_LINK,
  ACADEMY_WELCOME_TEXT_BEFORE_LINK,
} from './Academy.constants';

type DashboardState =
  | { status: 'loading' }
  | { status: 'ready'; data: TeacherAcademyDashboard }
  | { status: 'error'; message: string };

interface AcademyProps {
  onOpenSkill?: (params: { skillKey: string }) => void;
}

export function Academy({ onOpenSkill }: AcademyProps = {}) {
  const theme = useTheme();
  const [dashboard, setDashboard] = useState<DashboardState>({ status: 'loading' });
  const [selectedAcademyAreaKey, setSelectedAcademyAreaKey] = useState<string>('');
  const [selectedTierKey, setSelectedTierKey] = useState<string>('');
  const [selectedSkillKey, setSelectedSkillKey] = useState<string | null>(null);
  const [isFullPageWelcomeAlertVisible, setIsFullPageWelcomeAlertVisible] = useState(false);

  const handleSkillFinished = useCallback((message: AcademySkillFinishedMessage) => {
    setDashboard((currentDashboard) => {
      if (currentDashboard.status !== 'ready') {
        return currentDashboard;
      }

      const updatedDashboard = applyAcademySkillFinishedUpdate(currentDashboard.data, message);
      if (updatedDashboard === currentDashboard.data) {
        return currentDashboard;
      }

      return {
        ...currentDashboard,
        data: updatedDashboard,
      };
    });
  }, []);

  useAcademyRealtime({ onSkillFinished: handleSkillFinished });

  useEffect(() => {
    AlertService.clearAlerts(AlertService.AlertKind.ACADEMY);
    const closeAcademyWelcomeAlert = AlertService.success(
      <>
        {ACADEMY_WELCOME_TEXT_BEFORE_LINK}{' '}
        <Link
          href={ACADEMY_WELCOME_HELP_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          {ACADEMY_WELCOME_LINK_TEXT}
        </Link>{' '}
        {ACADEMY_WELCOME_TEXT_AFTER_LINK}
      </>,
      false,
      AlertService.AlertKind.ACADEMY,
    );

    return closeAcademyWelcomeAlert;
  }, []);

  useEffect(() => {
    let isCurrent = true;

    getTeacherAcademyDashboard()
      .then((data) => {
        if (isCurrent) {
          const defaultArea =
            data.academyAreas.find((area) => area.academyAreaKey === data.defaultAcademyAreaKey) ||
            data.academyAreas[0];

          setDashboard({ status: 'ready', data });
          setSelectedAcademyAreaKey(data.defaultAcademyAreaKey);
          setSelectedTierKey(getDefaultTierKey(defaultArea));
        }
      })
      .catch((error: unknown) => {
        if (isCurrent) {
          if (error instanceof AcademyAccessDeniedError) {
            window.location.assign('/experience');
            return;
          }

          setDashboard({
            status: 'error',
            message: error instanceof Error ? error.message : ACADEMY_REQUEST_FAILED_MESSAGE,
          });
        }
      });

    return () => {
      isCurrent = false;
    };
  }, []);

  if (dashboard.status === 'loading') {
    return (
      <Box className={academyClassNames.shell} sx={academyStyles.shell(theme)}>
        <Typography sx={academyStyles.statePanel()} role="status">
          {ACADEMY_LOADING_MESSAGE}
        </Typography>
      </Box>
    );
  }

  if (dashboard.status === 'error') {
    return (
      <Box className={academyClassNames.shell} sx={academyStyles.shell(theme)}>
        <Typography sx={academyStyles.statePanel(true)} role="alert">
          {dashboard.message}
        </Typography>
      </Box>
    );
  }

  const dashboardData = dashboard.data;

  if (dashboardData.academyAreas.length === 0) {
    const emptyTitle = dashboardData.hasAcademyPlans
      ? ACADEMY_NO_SKILLS_TITLE
      : ACADEMY_UNAVAILABLE_TITLE;
    const emptyMessage = dashboardData.hasAcademyPlans
      ? ACADEMY_NO_SKILLS_WITH_PLAN_MESSAGE
      : ACADEMY_NO_ACTIVE_PLAN_MESSAGE;

    return (
      <Box className={academyClassNames.shell} sx={academyStyles.shell(theme)}>
        <Box component="section" sx={academyStyles.emptyState()} aria-labelledby="empty-title">
          <Typography id="empty-title" component="h1" variant="h1" sx={academyStyles.emptyTitle()}>
            {emptyTitle}
          </Typography>
          <Typography sx={academyStyles.emptyText()}>{emptyMessage}</Typography>
        </Box>
      </Box>
    );
  }

  const selectedArea =
    dashboardData.academyAreas.find((area) => area.academyAreaKey === selectedAcademyAreaKey) ||
    dashboardData.academyAreas[0];
  const effectiveTierKey = getEffectiveTierKey(selectedArea, selectedTierKey);
  const selectedTierView = getTierView(selectedArea, effectiveTierKey);
  function handleAcademyAreaChange(academyAreaKey: string) {
    const nextArea = dashboardData.academyAreas.find((area) => area.academyAreaKey === academyAreaKey);

    setSelectedAcademyAreaKey(academyAreaKey);
    setSelectedTierKey(getDefaultTierKey(nextArea));
    setSelectedSkillKey(null);
  }

  function handleTierSelect(tierKey: string) {
    setSelectedTierKey(tierKey);
    setSelectedSkillKey(null);
  }

  function handleOpenSkill(skillKey: string) {
    if (onOpenSkill) {
      onOpenSkill({ skillKey });
      return;
    }

    setSelectedSkillKey(skillKey);
  }

  const academyAreaOptions = dashboardData.academyAreas.map((area) => ({
    id: area.academyAreaKey,
    name: area.label,
  }));
  return (
    <>
      <AlertComponent
        alertSx={academyStyles.welcomeAlert(theme)}
        hideIcon
        kind={AlertService.AlertKind.ACADEMY}
        onFullPageVisibilityChange={setIsFullPageWelcomeAlertVisible}
        placement="full-page"
      />
      <Box
        className={academyClassNames.shell}
        sx={academyStyles.shell(theme, isFullPageWelcomeAlertVisible)}
      >
        <Box sx={academyStyles.content(theme)}>
          <Box component="section" sx={academyStyles.overview(theme)} aria-labelledby="academy-title">
            <Box sx={academyStyles.heroCopy(theme)}>
              <Box sx={academyStyles.titleRow()}>
                <Typography
                  id="academy-title"
                  component="h1"
                  variant="h1"
                  sx={academyStyles.title()}
                >
                  {selectedArea.heading}
                </Typography>
                <PopupMenu
                  ariaLabel={ACADEMY_AREA_MENU_ARIA_LABEL}
                  items={academyAreaOptions}
                  onSelect={handleAcademyAreaChange}
                />
              </Box>
              <Typography sx={academyStyles.description()}>{selectedArea.description}</Typography>
            </Box>

            <TierProgress
              area={selectedArea}
              selectedTierKey={effectiveTierKey}
              onSelectTier={handleTierSelect}
            />

            <Box
              component="dl"
              sx={academyStyles.metricGrid(theme)}
              aria-label={`${selectedArea.label} metrics`}
            >
              {selectedArea.metrics.map((metric) => (
                <Box key={metric.metricKey} sx={academyStyles.metricItem()}>
                  <Typography component="dt" sx={academyStyles.metricLabel()}>
                    {metric.label}
                  </Typography>
                  <Typography component="dd" sx={academyStyles.metricValue()}>
                    {metric.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <RecommendedLessons
            lessons={selectedTierView.recommendedLessons}
            onOpenSkill={handleOpenSkill}
          />
          <SkillPaths
            area={selectedTierView}
            onOpenSkill={handleOpenSkill}
            selectedSkillKey={selectedSkillKey}
          />
        </Box>
      </Box>
    </>
  );
}
